import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ChangePassword from "@/components/ChangePassword";
import {
  LayoutDashboard, Users, ShieldCheck, CalendarDays, Clock, AlertTriangle,
  Pill, Bell, ScrollText, Banknote, LogOut, MapPin, Timer, Menu, KeyRound,
  FileSpreadsheet, HandCoins, GraduationCap, Share2, Monitor, UserSquare2, ClipboardCheck,
  FileText,
} from "lucide-react";

const NAV = {
  admin: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/individuals", label: "Individuals", icon: UserSquare2 },
    { to: "/staff", label: "Staff", icon: Users },
    { to: "/certifications", label: "Certifications", icon: ShieldCheck },
    { to: "/locations", label: "Locations", icon: MapPin },
    { to: "/shifts", label: "Scheduling", icon: CalendarDays },
    { to: "/timesheets", label: "Timesheets", icon: Clock },
    { to: "/review-queue", label: "Review Queue", icon: ClipboardCheck },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/mar", label: "Digital MAR", icon: Pill },
    { to: "/training", label: "Training", icon: GraduationCap },
    { to: "/notifications", label: "Alerts", icon: Bell },
    { to: "/audit-log", label: "Audit Log", icon: ScrollText },
    { to: "/audit-links", label: "Inspector Links", icon: Share2 },
    { to: "/finance", label: "Financials", icon: Banknote },
    { to: "/invoices", label: "Invoices", icon: FileSpreadsheet },
  ],
  rn: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/individuals", label: "Individuals", icon: UserSquare2 },
    { to: "/certifications", label: "Delegation", icon: ShieldCheck },
    { to: "/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/mar", label: "Digital MAR", icon: Pill },
    { to: "/timesheets", label: "Timesheets", icon: Clock },
    { to: "/review-queue", label: "Review Queue", icon: ClipboardCheck },
    { to: "/open-shifts", label: "Open Shifts", icon: HandCoins },
    { to: "/training", label: "Training", icon: GraduationCap },
    { to: "/notifications", label: "Alerts", icon: Bell },
  ],
  dsp: [
    { to: "/", label: "My Day", icon: LayoutDashboard },
    { to: "/individuals", label: "My People", icon: UserSquare2 },
    { to: "/clock", label: "Clock In/Out", icon: Timer },
    { to: "/my-schedule", label: "Schedule", icon: CalendarDays },
    // Batch 1 (2026-05-22): DSP-facing entry to reach the documentation
    // workflow for assigned EVV-imported visits. Routes to the existing
    // Timesheets page, which is DSP-aware and correctly authorizes writes
    // to `user_id == self` per services/authz + routers/timesheets.py.
    { to: "/timesheets", label: "My Documentation", icon: FileText },
    { to: "/open-shifts", label: "Open Shifts", icon: HandCoins },
    { to: "/mar", label: "MAR", icon: Pill },
    { to: "/incidents", label: "Report", icon: AlertTriangle },
    { to: "/certifications", label: "My Creds", icon: ShieldCheck },
    { to: "/training", label: "Training", icon: GraduationCap },
    { to: "/notifications", label: "Alerts", icon: Bell },
  ],
};

// Top 4 items shown in mobile bottom tab bar — picked for thumb-zone access
const BOTTOM_TABS = {
  admin: ["/", "/shifts", "/incidents", "/notifications"],
  rn: ["/", "/incidents", "/mar", "/notifications"],
  dsp: ["/", "/clock", "/mar", "/incidents"],
};

function NavList({ items, onNavigate, compact = false }) {
  return (
    <nav className={compact ? "flex md:flex-col gap-1" : "flex flex-col gap-1"}>
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={({ isActive }) =>
              `shrink-0 flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] transition-colors ${
                isActive ? "bg-primary text-primary-foreground"
                         : "text-muted-foreground hover:bg-accent hover:text-primary"
              }`
            }
          >
            <Icon size={17} strokeWidth={2} />
            <span>{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function BrandHeader() {
  return (
    <div className="p-6 flex items-center gap-3 border-b border-border">
      <img src="/abelia-logo.jpg" alt="Abelia" className="w-11 h-11 rounded-xl object-cover bg-white ring-1 ring-border"/>
      <div>
        <div className="font-semibold text-primary leading-tight text-lg" style={{fontFamily:'Outfit'}}>Abelia</div>
        <div className="accp-label text-[9px] leading-tight">Compliance Portal</div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const items = NAV[user?.role] || [];
  const bottomKeys = BOTTOM_TABS[user?.role] || [];
  const bottomItems = bottomKeys.map((k) => items.find((i) => i.to === k)).filter(Boolean);

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:block md:w-64 md:shrink-0 md:h-screen md:sticky md:top-0 border-r border-border bg-white/70 backdrop-blur">
        <BrandHeader />
        <div className="p-3">
          <NavList items={items} compact />
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/85 border-b border-border/60">
          <div className="px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
            {/* Mobile hamburger + brand */}
            <div className="flex items-center gap-3 md:hidden">
              <Sheet open={drawer} onOpenChange={setDrawer}>
                <SheetTrigger asChild>
                  <button className="w-10 h-10 rounded-xl border border-border bg-white flex items-center justify-center"
                          data-testid="mobile-menu-btn" aria-label="Open menu">
                    <Menu size={18} className="text-primary"/>
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72">
                  <BrandHeader />
                  <div className="p-3">
                    <NavList items={items} onNavigate={() => setDrawer(false)} />
                  </div>
                  <div className="px-3 pb-6 mt-2 border-t border-border pt-3 space-y-1">
                    <button onClick={() => { setDrawer(false); setPwOpen(true); }}
                            className="accp-btn-ghost w-full justify-start"
                            data-testid="mobile-change-password-btn">
                      <KeyRound size={16} /> Change password
                    </button>
                    <button onClick={() => { setDrawer(false); doLogout(); }} className="accp-btn-ghost w-full justify-start">
                      <LogOut size={16} /> Sign out
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
              <img src="/abelia-logo.jpg" alt="Abelia" className="w-9 h-9 rounded-lg object-cover bg-white ring-1 ring-border"/>
            </div>

            {/* Title block */}
            <div className="min-w-0 flex-1 md:flex-initial">
              <div className="accp-label hidden md:block">Role · {user?.role?.toUpperCase()}</div>
              <div className="text-primary font-semibold truncate text-sm md:text-base" style={{fontFamily:'Outfit'}}>
                {user?.name}
                <span className="md:hidden ml-2 text-[10px] uppercase tracking-wider text-muted-foreground font-normal">{user?.role}</span>
              </div>
            </div>

            {/* Sign out — desktop only (mobile uses drawer) */}
            <div className="hidden md:flex items-center gap-2">
              <button onClick={() => nav("/account/devices")} className="accp-btn-ghost"
                      data-testid="devices-btn">
                <Monitor size={16} /> Devices
              </button>
              <button onClick={() => setPwOpen(true)} className="accp-btn-ghost"
                      data-testid="change-password-btn">
                <KeyRound size={16} /> Change password
              </button>
              <button onClick={doLogout} className="accp-btn-ghost" data-testid="logout-btn">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </div>
        </header>

        <div className="px-4 py-5 md:px-10 md:py-10">{children}</div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-border safe-bottom">
        <ul className="grid" style={{gridTemplateColumns:`repeat(${bottomItems.length}, 1fr)`}}>
          {bottomItems.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.to === "/"}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold tracking-wide ${
                      isActive ? "text-primary" : "text-muted-foreground"
                    }`
                  }
                  data-testid={`tab-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  <Icon size={20} strokeWidth={2}/>
                  <span>{it.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {pwOpen && <ChangePassword onClose={() => setPwOpen(false)} />}
    </div>
  );
}
